import Phaser from 'phaser';
import { THEME } from '@/config/theme';
import { AudioManager } from '@/systems/AudioManager';
import type { AudioFileKey } from '@/config/audioPacks';

export type ButtonStyle = 'primary' | 'secondary' | 'danger';

export interface ButtonSize {
  width: number;
  height: number;
  fontSize: string;
}

export type ButtonSizeMap = Record<ButtonStyle, ButtonSize>;
export type ButtonType = 'normal' | 'back' | 'cancel' | 'other';

export interface CreateButtonOptions {
  x: number;
  y: number;
  text: string;
  onClick: () => void;
  style?: ButtonStyle;
  sizes?: Partial<ButtonSizeMap>;
  stopPropagation?: boolean;
  buttonType: ButtonType;
}

const DEFAULT_SIZES: ButtonSizeMap = {
  primary: { width: 360, height: 72, fontSize: THEME.sizes.subheading },
  secondary: { width: 280, height: 60, fontSize: THEME.sizes.bodyLarge },
  danger: { width: 100, height: 30, fontSize: THEME.sizes.tiny },
};

const STYLE_CONFIG: Record<ButtonStyle, {
  fillColor: number;
  strokeColor: number;
  textColor: string;
  hoverFill: number;
}> = {
  primary: {
    fillColor: THEME.colors.magentaHex,
    strokeColor: THEME.colors.magentaDarkHex,
    textColor: THEME.colors.text,
    hoverFill: THEME.colors.magentaDarkHex,
  },
  secondary: {
    fillColor: THEME.colors.backgroundLightHex,
    strokeColor: THEME.colors.cyanHex,
    textColor: THEME.colors.cyan,
    hoverFill: THEME.colors.backgroundPanelHex,
  },
  danger: {
    fillColor: THEME.colors.backgroundPanelHex,
    strokeColor: 0x994444,
    textColor: '#aa6666',
    hoverFill: 0x442222,
  },
};

function applyButtonEnabled(container: Phaser.GameObjects.Container, enabled: boolean): void {
  container.setAlpha(enabled ? 1 : 0.45);
  container.iterate((obj: Phaser.GameObjects.GameObject) => {
    if (obj instanceof Phaser.GameObjects.Rectangle) {
      obj.disableInteractive();
      if (enabled) obj.setInteractive({ useHandCursor: true });
    }
  });
}

export function setButtonEnabled(button: Phaser.GameObjects.Container | null, enabled: boolean): void {
  if (!button) return;
  applyButtonEnabled(button, enabled);
}

export function setButtonLabel(button: Phaser.GameObjects.Container | null, text: string): void {
  if (!button) return;
  button.iterate((obj: Phaser.GameObjects.GameObject) => {
    if (obj instanceof Phaser.GameObjects.Text) obj.setText(text);
  });
}

const BUTTON_SFX_MAP: Record<ButtonType, AudioFileKey> = {
  normal: 'click_button',
  back: 'click_button',
  cancel: 'click_button',
  other: 'click_option',
};

function playSound(scene: Phaser.Scene, buttonType: ButtonType): void {
  const key = BUTTON_SFX_MAP[buttonType] ?? 'click_button';
  AudioManager.get(scene).playSfx(key);
}

export function createButton(scene: Phaser.Scene, options: CreateButtonOptions): Phaser.GameObjects.Container {
  const container = scene.add.container(options.x, options.y);
  const style: ButtonStyle = options.style ?? 'primary';
  const size = (options.sizes?.[style]) ?? DEFAULT_SIZES[style];
  const { fillColor, strokeColor, textColor, hoverFill } = STYLE_CONFIG[style];

  const bg = scene.add.rectangle(0, 0, size.width, size.height, fillColor, 1);
  bg.setStrokeStyle(THEME.button.borderWidth, strokeColor);

  const label = scene.add.text(0, 0, options.text, {
    fontFamily: THEME.fonts.heading,
    fontSize: size.fontSize,
    color: textColor,
  }).setOrigin(0.5);

  container.add([bg, label]);
  bg.setName('bg');
  label.setName('label');
  bg.setInteractive({ useHandCursor: true });
  bg.on('pointerover', () => bg.setFillStyle(hoverFill));
  bg.on('pointerout', () => bg.setFillStyle(fillColor));
  bg.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
    if (options.stopPropagation) pointer.event.stopPropagation();
    playSound(scene, options.buttonType);
    options.onClick();
  });

  return container;
}

export interface CreateSmallButtonOptions {
  x: number;
  y: number;
  text: string;
  onClick: () => void;
  width?: number;
  height?: number;
}

export function createSmallButton(scene: Phaser.Scene, options: CreateSmallButtonOptions): Phaser.GameObjects.Container {
  const container = scene.add.container(options.x, options.y);
  const bg = scene.add.rectangle(0, 0, options.width ?? 120, options.height ?? 40, THEME.colors.backgroundPanelHex, 1);
  bg.setStrokeStyle(2, THEME.colors.cyanHex);
  bg.setInteractive({ useHandCursor: true });

  const label = scene.add.text(0, 0, options.text, {
    fontFamily: THEME.fonts.heading,
    fontSize: THEME.sizes.small,
    color: THEME.colors.cyan,
  }).setOrigin(0.5);

  container.add([bg, label]);
  bg.setName('bg');
  label.setName('label');
  bg.on('pointerover', () => bg.setFillStyle(THEME.colors.backgroundLightHex, 1));
  bg.on('pointerout', () => bg.setFillStyle(THEME.colors.backgroundPanelHex, 1));
  bg.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
    pointer.event.stopPropagation();
    playSound(scene, 'normal');
    options.onClick();
  });
  return container;
}

export interface CreateInlineButtonOptions {
  x: number;
  y: number;
  text: string;
  onClick: () => void;
  width: number;
  height?: number;
}

export function createInlineButton(scene: Phaser.Scene, options: CreateInlineButtonOptions): Phaser.GameObjects.Container {
  const container = scene.add.container(options.x, options.y);
  const bg = scene.add.rectangle(0, 0, options.width, options.height ?? 44, THEME.colors.backgroundPanelHex, 1);
  bg.setStrokeStyle(2, THEME.colors.cyanHex);
  bg.setInteractive({ useHandCursor: true });

  const label = scene.add.text(0, 0, options.text, {
    fontFamily: THEME.fonts.heading,
    fontSize: THEME.sizes.small,
    color: THEME.colors.cyan,
  }).setOrigin(0.5);

  container.add([bg, label]);
  bg.setName('bg');
  label.setName('label');
  bg.on('pointerover', () => bg.setFillStyle(THEME.colors.backgroundLightHex, 1));
  bg.on('pointerout', () => bg.setFillStyle(THEME.colors.backgroundPanelHex, 1));
  bg.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
    pointer.event.stopPropagation();
    playSound(scene, 'normal');
    options.onClick();
  });

  return container;
}

export interface CreateModalButtonOptions {
  x: number;
  y: number;
  text: string;
  onClick: () => void;
  style: ButtonStyle;
  sizes?: Partial<ButtonSizeMap>;
}

export interface CreateBackButtonOptions {
  x: number;
  y: number;
  onClick: () => void;
  text?: string;
  stopPropagation?: boolean;
}

const BACK_BUTTON_SIZE: ButtonSize = {
  width: 120,
  height: 40,
  fontSize: THEME.sizes.small,
};

export function createModalButton(scene: Phaser.Scene, options: CreateModalButtonOptions): Phaser.GameObjects.Container {
  const container = scene.add.container(options.x, options.y);
  const style = options.style;

  // Default sizes for modal buttons
  const MODAL_DEFAULT_SIZES: ButtonSizeMap = {
    primary: { width: 280, height: 60, fontSize: THEME.sizes.subheading },
    secondary: { width: 220, height: 60, fontSize: THEME.sizes.bodyLarge },
    danger: { width: 100, height: 30, fontSize: THEME.sizes.tiny },
  };

  // Use provided size or fall back to defaults
  const size = (options.sizes?.[style]) ?? MODAL_DEFAULT_SIZES[style];
  const width = size.width;
  const height = size.height;
  const fontSize = size.fontSize;

  const isPrimary = style === 'primary';
  const fillColor = isPrimary ? THEME.colors.magentaHex : THEME.colors.backgroundLightHex;
  const strokeColor = isPrimary ? THEME.colors.magentaDarkHex : THEME.colors.cyanHex;
  const textColor = isPrimary ? THEME.colors.text : THEME.colors.cyan;
  const hoverFill = isPrimary ? THEME.colors.magentaDarkHex : THEME.colors.backgroundPanelHex;

  const bg = scene.add.rectangle(0, 0, width, height, fillColor, 1);
  bg.setStrokeStyle(THEME.button.borderWidth, strokeColor);

  const label = scene.add.text(0, 0, options.text, {
    fontFamily: THEME.fonts.heading,
    fontSize,
    color: textColor,
  }).setOrigin(0.5);

  container.add([bg, label]);
  bg.setName('bg');
  label.setName('label');
  bg.setInteractive({ useHandCursor: true });
  bg.on('pointerover', () => bg.setFillStyle(hoverFill));
  bg.on('pointerout', () => bg.setFillStyle(fillColor));
  bg.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
    pointer.event.stopPropagation();
    playSound(scene, 'normal');
    options.onClick();
  });

  return container;
}

export function createBackButton(scene: Phaser.Scene, options: CreateBackButtonOptions): Phaser.GameObjects.Container {
  return createButton(scene, {
    x: options.x,
    y: options.y,
    text: options.text ?? '← BACK',
    onClick: options.onClick,
    style: 'secondary',
    sizes: { secondary: BACK_BUTTON_SIZE },
    stopPropagation: options.stopPropagation,
    buttonType: 'back',
  });
}
